import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { forgotPassword } from '@/api';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_URL = '/logo.png';

export default function ForgotPassword() {
  const nav = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
      toast.success(t('auth.resetEmailSent'));
    } catch (err) {
      if (err.response?.status === 429) toast.error(t('auth.tooManyRequests'));
      else { toast.success(t('auth.resetEmailSent')); setSent(true); }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-3">
              <button onClick={() => nav('/login')} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
              <Link to="/"><img src={LOGO_URL} alt="ServiceGo" className="h-12 sm:h-16 w-auto" /></Link>
            </div>
            <LanguageSelector />
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 sm:px-6 py-12 sm:py-20">
        {sent ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-green-600" /></div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('ui.emailSent')}</h2>
            <p className="text-slate-600 mb-6">{t('auth.resetEmailSent')}</p>
            <Button onClick={() => nav('/login')} className="bg-[#5848FF] hover:bg-[#4638EF] text-white rounded-xl h-11 px-8">{t('auth.backToLogin')}</Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('auth.resetPassword')}</h2>
            <p className="text-slate-600 mb-6">{t('auth.resetPasswordDesc')}</p>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input name="email" type="email" placeholder={t('auth.email')} value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl pl-10" required />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-[#5848FF] hover:bg-[#4638EF] text-white rounded-xl">{loading ? t('common.loading') : t('auth.sendResetLink')}</Button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
